import React, { useState, useEffect } from 'react';
import { X, Upload, CheckCircle, Building, CreditCard, Copy } from 'lucide-react';
import { toast } from 'sonner';

const PaymentConfirmationModal = ({ isOpen, onClose, onSuccess, paymentData }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);

  const bankDetails = {
    accountName: 'Netix Fibernet',
    accountNumber: '33738100019631',
    ifscCode: 'BARB0MADHUB',
    bankName: 'Bank of Baroda',
    branch: 'MADHUBANI, BIHAR',
    upiId: 'NETIXFIBERNET@PTYES'
  };

  useEffect(() => {
    if (isOpen && paymentData) {
      generateQRCode();
    }
  }, [isOpen, paymentData]);

  const generateQRCode = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/customer/payment/generate-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: paymentData.totalAmount,
          upiId: bankDetails.upiId,
          name: bankDetails.accountName
        })
      });

      if (response.ok) {
        const data = await response.json();
        setQrCodeUrl(data.qrCodeUrl);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setScreenshot(file);
      toast.success('Screenshot attached');
    }
  };

  const handleSubmit = async () => {
    if (!screenshot) {
      toast.error('Please attach payment screenshot');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('screenshot', screenshot);
      formData.append('paymentData', JSON.stringify(paymentData));

      const response = await fetch(`${backendUrl}/api/customer/payment/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        toast.error('Failed to submit payment');
        return;
      }

      toast.success('Payment submitted successfully');
      onSuccess();
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error('Failed to submit payment');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (!isOpen || !paymentData) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 flex justify-between items-center rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold">Payment Confirmation</h2>
            <p className="text-purple-100 text-sm mt-1">Complete your payment</p>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer Details */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-blue-900 mb-3">Customer Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="font-semibold">Name:</span> {paymentData.name}</div>
              <div><span className="font-semibold">Mobile:</span> {paymentData.mobile}</div>
              <div><span className="font-semibold">Email:</span> {paymentData.email}</div>
              <div><span className="font-semibold">Service:</span> {paymentData.service}</div>
              {paymentData.officeName && <div><span className="font-semibold">Office:</span> {paymentData.officeName}</div>}
            </div>
          </div>

          {/* Invoice Details */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <h3 className="font-bold text-green-900 mb-3">Selected Invoices</h3>
            <div className="space-y-2">
              {paymentData.invoices?.map((invoice, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span>{invoice.invoiceNumber}</span>
                  <span className="font-semibold">₹{invoice.amount?.toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t-2 border-green-300 mt-3 pt-3 flex justify-between">
              <span className="font-bold text-lg">Total Amount:</span>
              <span className="font-bold text-2xl text-green-700">₹{paymentData.totalAmount?.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* QR Code Section */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
              <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Scan QR Code to Pay
              </h3>
              <div className="bg-white p-4 rounded-lg flex justify-center">
                {qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="Payment QR Code" className="w-48 h-48" />
                ) : (
                  <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                    <p className="text-gray-500 text-sm">Generating QR Code...</p>
                  </div>
                )}
              </div>
              <div className="mt-3 text-center">
                <p className="text-sm font-semibold text-purple-900">UPI ID</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <p className="text-sm text-purple-700">{bankDetails.upiId}</p>
                  <button
                    onClick={() => copyToClipboard(bankDetails.upiId)}
                    className="text-purple-600 hover:text-purple-800"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Bank Details Section */}
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
              <h3 className="font-bold text-orange-900 mb-3 flex items-center gap-2">
                <Building className="w-5 h-5" />
                Bank Account Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Account Name:</span>
                  <span>{bankDetails.accountName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Account Number:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{bankDetails.accountNumber}</span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.accountNumber)}
                      className="text-orange-600 hover:text-orange-800"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">IFSC Code:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{bankDetails.ifscCode}</span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.ifscCode)}
                      className="text-orange-600 hover:text-orange-800"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Bank:</span>
                  <span>{bankDetails.bankName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Branch:</span>
                  <span>{bankDetails.branch}</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded text-xs">
                <p className="font-semibold text-yellow-900">Bank Payment Instruction:</p>
                <p className="text-yellow-800 mt-1">After making payment via bank transfer, please share the transaction details with us at <span className="font-semibold">accounts@netixfibernet.com</span></p>
              </div>
            </div>
          </div>

          {/* Screenshot Upload */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Attach Payment Screenshot (For UPI/QR Code Payment)
            </h3>
            <label className="block cursor-pointer">
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                screenshot ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}>
                {screenshot ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle className="w-6 h-6" />
                    <span className="font-medium">{screenshot.name}</span>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click to upload payment screenshot</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG (Max 5MB)</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !screenshot}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-semibold disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmationModal;
